import {
  ADMIN_QA_CALLING_FROM_FUNCTION,
  TRANSACTIONAL_EMAIL_TEMPLATES,
  type TransactionalEmailPayloadShape,
} from './constants'
import { getQaPayloadRootFieldKeys } from './qa-field-fixtures'
import type { MemberContext } from './presets'

export type EmailContactEventRequestBody = Record<string, unknown>

const PAYLOAD_ROOT_FIELD_KEYS = new Set(['everflowTransactionId', 'utm', 'planName'])

const JSON_ARRAY_FIELD_KEYS = new Set(['entitled_ecommerce_sku_keys', 'entitledEcommerceSkuKeys'])

const JSON_OBJECT_ARRAY_FIELD_KEYS = new Set([
  'ecommerce_sku_mapping_bonus_eligibilities',
  'ecommerceSkuMappingBonusEligibilities',
])

const BOOLEAN_FIELD_KEYS = new Set([
  'is_new_user',
  'includes_content',
  'trial_period',
  'share_watch_link_clicked',
  'prospect_email_comm_br',
  'is_trial',
])

function parseBooleanField (value: string): boolean | undefined {
  const normalized = value.trim().toLowerCase()
  if (normalized === 'true' || normalized === '1') {
    return true
  }
  if (normalized === 'false' || normalized === '0') {
    return false
  }
  return undefined
}

function coerceFieldValue (key: string, raw: string): unknown {
  if (JSON_ARRAY_FIELD_KEYS.has(key) || JSON_OBJECT_ARRAY_FIELD_KEYS.has(key)) {
    try {
      return JSON.parse(raw)
    } catch {
      return raw
    }
  }
  if (BOOLEAN_FIELD_KEYS.has(key)) {
    const boolVal = parseBooleanField(raw)
    return boolVal !== undefined ? boolVal : raw
  }
  if (key === 'next_bill_amount') {
    const num = Number(raw)
    return Number.isFinite(num) ? num : raw
  }
  if (key === 'shared_video_id') {
    const num = Number(raw)
    return Number.isFinite(num) ? num : raw
  }
  return raw
}

function getTemplateShape (templateId: string): TransactionalEmailPayloadShape {
  const template = TRANSACTIONAL_EMAIL_TEMPLATES.find((t) => t.id === templateId)
  return template?.payloadShape || 'generic'
}

function getEventName (templateId: string): string {
  const template = TRANSACTIONAL_EMAIL_TEMPLATES.find((t) => t.id === templateId)
  return template?.eventName || templateId
}

function buildFields (
  templateId: string,
  fieldValues: Record<string, string>,
): Record<string, unknown> {
  const eventName = getEventName(templateId)
  const rootKeys = new Set(getQaPayloadRootFieldKeys(templateId))
  const fields: Record<string, unknown> = { event_name: eventName }
  Object.entries(fieldValues).forEach(([key, value]) => {
    if (PAYLOAD_ROOT_FIELD_KEYS.has(key) || rootKeys.has(key)) {
      return
    }
    if (value !== undefined && value !== null && String(value).length > 0) {
      fields[key] = coerceFieldValue(key, String(value))
    }
  })
  if (fields.is_trial === undefined && fieldValues.trial_period) {
    const trial = parseBooleanField(fieldValues.trial_period)
    if (trial !== undefined) {
      fields.is_trial = trial
      if (fields.trial_period === undefined) {
        fields.trial_period = trial
      }
    }
  }
  return fields
}

function getPayloadRootFields (
  templateId: string,
  fieldValues: Record<string, string>,
): Record<string, unknown> {
  const rootFields: Record<string, unknown> = {}
  const rootKeys = getQaPayloadRootFieldKeys(templateId)
  rootKeys.forEach((key) => {
    const value = fieldValues[key]
    if (value !== undefined && value !== null && String(value).trim().length > 0) {
      rootFields[key] = String(value).trim()
    }
  })
  if (templateId === 'GIFT_SUBSCRIPTION_PURCHASER_RECEIPT') {
    const planName = fieldValues.planName || fieldValues.plan_name
    if (planName) {
      rootFields.planName = planName
    }
  }
  return rootFields
}

/**
 * Maps Admin UI state to Brooklyn `POST /v1/billing/email-contact-event` body.
 */
export function buildEmailContactEventPayload (
  templateId: string,
  fieldValues: Record<string, string>,
  member: MemberContext,
): EmailContactEventRequestBody {
  const shape = getTemplateShape(templateId)
  const { email } = member
  const { userUuid } = member

  if (shape === 'gift_recipient') {
    return {
      eventType: 'gift',
      userUuid,
      gifterName: fieldValues.gifterName || '',
      recipientEmail: fieldValues.recipientEmail || email,
      recipientFirstName: fieldValues.recipientFirstName || '',
      recipientLastName: fieldValues.recipientLastName || '',
      registrationLanguage: fieldValues.registrationLanguage || 'en',
      deliveryDate: fieldValues.deliveryDate,
      tokenURL: fieldValues.tokenURL,
      message: fieldValues.message || '',
      expiresAt: fieldValues.expiresAt,
      planName: fieldValues.planName,
      token: fieldValues.token,
      callingFromFunction: ADMIN_QA_CALLING_FROM_FUNCTION,
    }
  }

  if (shape === 'new_user_password_reset') {
    return {
      eventType: 'create_new_user_password_reset_email_capture',
      email,
      userUuid,
      optin: true,
      fields: {
        event_name: getEventName(templateId),
        registration_language: fieldValues.registration_language || 'en',
        password_reset_url: fieldValues.password_reset_url,
      },
      callingFromFunction: ADMIN_QA_CALLING_FROM_FUNCTION,
    }
  }

  if (shape === 'password_reset') {
    return {
      email,
      userUuid,
      optin: true,
      fields: {
        event_name: 'PASSWORD_RESET',
        registration_language: fieldValues.registration_language || 'en',
        password_reset_url: fieldValues.password_reset_url,
        ...(fieldValues.first_name ? { first_name: fieldValues.first_name } : {}),
      },
      callingFromFunction: ADMIN_QA_CALLING_FROM_FUNCTION,
    }
  }

  const fields = buildFields(templateId, fieldValues)
  return {
    email,
    userUuid,
    optin: true,
    fields,
    callingFromFunction: ADMIN_QA_CALLING_FROM_FUNCTION,
    ...getPayloadRootFields(templateId, fieldValues),
  }
}
